import type { AuthenticatedPageShellVariant } from '@/components/AuthenticatedPageShell';

/**
 * How the shared context rail behaves on a route:
 * - 'context': rail is available immediately with page-level context.
 * - 'selection': rail is available only once something is selected in the center
 *   canvas (a conversation, a story); otherwise the rail stays unavailable.
 * - 'inspector': a future collapsible inspector for canvas/map routes -- the
 *   canvas keeps full width until that inspector is built and registers content.
 * - 'none': the route never shows a rail.
 */
export type AuthenticatedRailAvailability = 'context' | 'selection' | 'inspector' | 'none';

export type AuthenticatedPageShellRoute = {
  path: string;
  variant: AuthenticatedPageShellVariant;
  match?: 'exact' | 'prefix';
  rail?: AuthenticatedRailAvailability;
};

/**
 * Explicit route-to-shell matrix for authenticated individual surfaces.
 * Keep the more specific routes before `/individual-dashboard`.
 */
export const AUTHENTICATED_INDIVIDUAL_PAGE_SHELL_ROUTES = [
  { path: '/individual-dashboard/life-cards', variant: 'canvas', rail: 'none' },
  { path: '/individual-dashboard/journey-map', variant: 'immersive', rail: 'inspector' },
  { path: '/individual-dashboard/geospatial-map', variant: 'wide', rail: 'inspector' },
  { path: '/individual-dashboard/circles', variant: 'canvas', rail: 'none' },
  { path: '/individual-dashboard/explore', variant: 'wide', rail: 'none' },
  { path: '/individual-dashboard/analytics', variant: 'wide', rail: 'none' },
  { path: '/individual-dashboard/articles', variant: 'reading', rail: 'none' },
  { path: '/individual-dashboard/public-archive', variant: 'reading', rail: 'none' },
  { path: '/individual-dashboard/public-vault', variant: 'wide', rail: 'none' },
  { path: '/individual-dashboard/public-identity', variant: 'form', rail: 'none' },
  { path: '/individual-dashboard/profile', variant: 'form', rail: 'none' },
  { path: '/individual-dashboard/settings', variant: 'form', rail: 'none' },
  { path: '/individual-dashboard/notifications', variant: 'standard', rail: 'none' },
  { path: '/individual-dashboard/pillars', variant: 'standard', rail: 'none' },
  { path: '/individual-dashboard/time-capsule', variant: 'standard', rail: 'none' },
  { path: '/individual-dashboard/trust-ledger', variant: 'standard', rail: 'none' },
  { path: '/individual-dashboard/proof-cards', variant: 'standard', rail: 'none' },
  { path: '/individual-dashboard/achievements', variant: 'standard', rail: 'none' },
  { path: '/individual-dashboard/milestones', variant: 'standard', rail: 'none' },
  { path: '/individual-dashboard/activity', variant: 'standard', rail: 'none' },
  { path: '/individual-dashboard/journal', variant: 'standard', rail: 'none' },
  { path: '/individual-dashboard/my-life', variant: 'standard', rail: 'none' },
  { path: '/individual-dashboard/my-people', variant: 'standard', rail: 'none' },
  { path: '/individual-dashboard/network', variant: 'standard', rail: 'none' },
  { path: '/individual-dashboard/feed', variant: 'standard', rail: 'none' },
  { path: '/individual-dashboard/support', variant: 'standard', rail: 'none' },
  { path: '/individual-dashboard', variant: 'standard', match: 'exact', rail: 'context' },
  { path: '/story-hub', variant: 'standard', match: 'prefix', rail: 'selection' },
  { path: '/family-ledger', variant: 'canvas', match: 'prefix', rail: 'inspector' },
  { path: '/pulse', variant: 'standard', match: 'prefix', rail: 'none' },
  { path: '/community', variant: 'standard', match: 'prefix', rail: 'none' },
  { path: '/articles', variant: 'reading', match: 'prefix', rail: 'none' },
  { path: '/circles', variant: 'standard', match: 'prefix', rail: 'none' },
  { path: '/billing', variant: 'standard', match: 'prefix', rail: 'none' },
  { path: '/support', variant: 'standard', match: 'prefix', rail: 'none' },
  { path: '/messages', variant: 'split', match: 'prefix', rail: 'selection' },
  { path: '/network', variant: 'canvas', match: 'prefix', rail: 'none' },
  { path: '/communities', variant: 'standard', match: 'prefix', rail: 'none' },
  { path: '/individual-profile', variant: 'standard', match: 'prefix', rail: 'none' },
] as const satisfies readonly AuthenticatedPageShellRoute[];

export function resolveAuthenticatedIndividualPageShell(
  pathname: string
): AuthenticatedPageShellVariant | null {
  const normalizedPath = pathname !== '/' ? pathname.replace(/\/+$/, '') : pathname;

  for (const route of AUTHENTICATED_INDIVIDUAL_PAGE_SHELL_ROUTES) {
    const match = 'match' in route ? route.match : 'exact';
    if (match === 'exact' && normalizedPath === route.path) return route.variant;
    if (
      match === 'prefix' &&
      (normalizedPath === route.path || normalizedPath.startsWith(`${route.path}/`))
    ) {
      return route.variant;
    }
  }

  return null;
}

/**
 * Whether the current route can ever show a context rail, and under what
 * condition. This does not mean a rail is currently registered -- it only
 * says whether the route is the kind of place a rail bridge is expected to
 * mount. Unmatched routes (e.g. outside the authenticated shell) get 'none'.
 */
export function resolveAuthenticatedIndividualRailAvailability(
  pathname: string
): AuthenticatedRailAvailability {
  const normalizedPath = pathname !== '/' ? pathname.replace(/\/+$/, '') : pathname;

  for (const route of AUTHENTICATED_INDIVIDUAL_PAGE_SHELL_ROUTES) {
    const match = 'match' in route ? route.match : 'exact';
    if (match === 'exact' && normalizedPath === route.path) return route.rail ?? 'none';
    if (
      match === 'prefix' &&
      (normalizedPath === route.path || normalizedPath.startsWith(`${route.path}/`))
    ) {
      return route.rail ?? 'none';
    }
  }

  return 'none';
}
